import {
  AiWizzardIllustration,
  DesktopAppDownloadIllustration,
} from '@carboncopyco/illustrations';
import {
  Button,
  InlineLink,
  Modal,
  TextBase,
  TextLargeParagraphTitle,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import type React from 'react';
import { useEffect } from 'react';

export interface ModalInfoData {
  learnMoreLink?: string;
  heading: string;
  description: string;
  buttonText: string;
  learnMoreText?: string;
  background?: React.ReactNode;
}
export interface InfoAlertProps {
  handleClose?: (fromX?: boolean) => void;
  open?: boolean;
}

const modalInfoData: ModalInfoData = {
  heading: 'New Feature Available : AI Wizard!',
  description:
    'Save time and boost your efficiency!\n' +
    'With just a simple request, you can now easily update the entire\n' +
    'context of your demo.',
  buttonText: 'Sounds magical!',
  background: <AiWizzardIllustration />,
  learnMoreText: 'See how to use it!',
  learnMoreLink: 'https://help.demostack.com/articles/ai-wizard',
};

export function NewFeatureModal({ handleClose, open }: InfoAlertProps) {
  const [isOpen, setIsOpen] = useBoolean(open ?? true);

  const {
    heading,
    description,
    buttonText,
    learnMoreLink,
    background,
    learnMoreText,
  } = modalInfoData;

  useEffect(() => {
    if (open !== undefined) {
      setIsOpen.set(open);
    }
  }, [open]);

  const handleLearnMoreClick = () => {
    if (!learnMoreLink) {
      return;
    }
    window.open(learnMoreLink, '_dsOpenExternal');
  };

  const closeModal = (fromX = true) => {
    handleClose?.(fromX);
    setIsOpen.setFalse();
  };

  return (
    <Modal
      open={isOpen}
      noPadding
      locked
      closeOnlyFromCloseIcon
      onClose={() => closeModal(true)}
    >
      <div className="flex flex-col w-[600px] h-auto justify-between">
        <div className="flex pb-5">
          {background ?? <DesktopAppDownloadIllustration />}
        </div>
        <div className="flex flex-col justify-between items-center h-full">
          <div className="flex flex-col justify-center items-center gap-2.5">
            <TextLargeParagraphTitle
              label={heading}
              color="text-ds-natural-600"
            />
            <TextBase
              textAlign="text-center"
              label={
                <div className="whitespace-pre">
                  {description}
                  <InlineLink
                    as={Button}
                    className="ml-1 inline-flex"
                    label={learnMoreText}
                    color="text-ds-black"
                    onClick={handleLearnMoreClick}
                  />
                </div>
              }
              color="text-ds-natural-600"
            />
          </div>
          <div className="flex pb-[30px] pt-10">
            <Button
              label={buttonText}
              onClick={() => {
                closeModal(false);
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
